import { ImportTargetColumn, PersonaFormElement } from '../types/import-target-column.type'

/**
 * Keeps only complete form-element fields used by invitation import mapping.
 * @param formElements {Array<Partial<PersonaFormElement>> | undefined} - Raw settings form elements.
 * @returns Slimmed form elements with required id, name, label, and weight.
 */
export function pickPersonaFormElements(formElements?: Array<Partial<PersonaFormElement>>): PersonaFormElement[] {
  if (!formElements?.length) return []

  return formElements.reduce<PersonaFormElement[]>((elements, element) => {
    if (element.id == null || element.weight == null || !element.name?.trim()) return elements

    elements.push({
      id: element.id,
      name: element.name,
      label: element.label?.trim() || formatPersonaFieldLabel(element.name),
      weight: element.weight
    })
    return elements
  }, [])
}

/**
 * Converts persona form elements into import mapping target columns.
 * Ordered by weight ascending (persona form order).
 * @param formElements {PersonaFormElement[]} - The form elements.
 * @returns The import mapping target columns.
 */
export function buildImportTargetColumnsFromFormElements(formElements: PersonaFormElement[]): ImportTargetColumn[] {
  return [...formElements]
    .filter((element) => !element.name.startsWith('_'))
    .sort((left, right) => left.weight - right.weight)
    .map((element) => ({
      field: element.name,
      header: element.label.trim() || formatPersonaFieldLabel(element.name)
    }))
}

/**
 * Formats a persona field label.
 * @param name {string} - The name.
 * @returns The formatted persona field label.
 */
export function formatPersonaFieldLabel(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

/**
 * Returns true when both column lists represent the same persona fields.
 * @param left {ImportTargetColumn[]} - The left column list.
 * @param right {ImportTargetColumn[]} - The right column list.
 * @returns Boolean indicating if the column lists are equal.
 */
export function areImportTargetColumnsEqual(left: ImportTargetColumn[], right: ImportTargetColumn[]): boolean {
  if (left.length !== right.length) return false

  return left.every((column, index) => column.field === right[index]?.field && column.header === right[index]?.header)
}
